import { useApp } from '@/store/app';
import { Card } from '@/components/ui/Card';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Sun, Moon, Trash2, Star } from 'lucide-react';
import { storage } from '@/lib/storage';
import { toast } from 'sonner';

export function SettingsView() {
  const { theme, setTheme, clearRecents, recents, favorites } = useApp();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
      <h1 className="text-3xl font-semibold tracking-tight mb-1">Settings</h1>
      <p className="text-sm text-fg-muted mb-8">Personalize Utility OS to your preferences.</p>

      <div className="space-y-3">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Theme</h3>
              <p className="text-xs text-fg-muted mt-0.5">
                Switch between light and dark modes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sun size={14} className="text-fg-subtle" />
              <Switch
                checked={theme === 'dark'}
                onChange={(v) => setTheme(v ? 'dark' : 'light')}
              />
              <Moon size={14} className="text-fg-subtle" />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold mb-3">Data</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="text-fg-muted">
                Recent activity
                <span className="text-fg-subtle ml-2 text-xs">
                  {recents.length} entries
                </span>
              </div>
              <Button variant="subtle" size="sm" onClick={clearRecents}>
                <Trash2 size={12} />
                Clear
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-fg-muted">
                Favorites
                <span className="text-fg-subtle ml-2 text-xs">
                  {favorites.length} starred
                </span>
              </div>
              <span className="text-xs text-fg-subtle flex items-center gap-1">
                <Star size={11} className="fill-warning text-warning" />
                stored locally
              </span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="text-fg-muted">Reset everything</div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  storage.clear();
                  toast.success('All local data cleared. Reload to start fresh.');
                  setTimeout(() => window.location.reload(), 800);
                }}
              >
                <Trash2 size={12} />
                Reset
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold mb-3">Keyboard shortcuts</h3>
          <div className="space-y-2 text-sm">
            <Shortcut keys={['⌘', 'K']} label="Open command bar" />
            <Shortcut keys={['⌘', 'J']} label="Open agent" />
            <Shortcut keys={['⌘', '⇧', 'L']} label="Toggle theme" />
            <Shortcut keys={['⌘', '/']} label="Go home" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Shortcut({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-muted">{label}</span>
      <div className="flex items-center gap-1">
        {keys.map((k) => (
          <kbd key={k}>{k}</kbd>
        ))}
      </div>
    </div>
  );
}
